import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  useConfigRequests,
  useConfigRequestsPendingCount,
  useUpdateConfigRequest,
} from '@/hooks/useConfigRequests'
import { DataTable, type ColumnDef } from '@/components/common/DataTable'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ConfigRequest,
  ConfigRequestFilter,
  SearchSettingsRequestPayload,
  UpdateConfigRequestPayload,
} from '@/types'
import { formatDate } from '@/lib/utils'
import { CONFIG_REQUEST_TARGET_LABELS, DISTRIBUTION_MODE_LABELS } from '@/lib/constants'
import { Check, X, Eye, ArrowRight } from 'lucide-react'

const KIND_LABELS: Record<string, string> = {
  initial_setup: 'Configuracion inicial',
  change_request: 'Cambio de configuracion',
}

const TARGET_LABELS = CONFIG_REQUEST_TARGET_LABELS

/** Card 183: who decides. Requests created before that card carry no
 * approver_scope — they are staff's, same as the explicit value. */
const APPROVER_LABELS: Record<string, string> = {
  staff: 'Staff',
  org: 'Dueno de org',
  owner_self: 'Dueno (propia)',
}

const APPROVER_FILTERS = [
  { value: 'all', label: 'Todos los aprobadores' },
  { value: 'staff', label: 'Decide staff' },
  { value: 'org', label: 'Decide el dueno de la org' },
  { value: 'owner_self', label: 'Propias del dueno' },
]

const TABS = [
  { value: 'pending', label: 'Pendientes' },
  { value: 'approved', label: 'Aprobadas' },
  { value: 'applied', label: 'Aplicadas' },
  { value: 'rejected', label: 'Rechazadas' },
  { value: 'all', label: 'Todas' },
]

export const RequestInboxPage = () => {
  const [activeTab, setActiveTab] = useState('pending')
  const [filters, setFilters] = useState<ConfigRequestFilter>({ page: 1, limit: 20 })

  const [selectedRequest, setSelectedRequest] = useState<ConfigRequest | null>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [showUpdate, setShowUpdate] = useState(false)
  const [updateForm, setUpdateForm] = useState<UpdateConfigRequestPayload>({
    status: 'approved',
    admin_notes: '',
    reason: '',
  })

  const currentFilters: ConfigRequestFilter = {
    ...filters,
    status: activeTab !== 'all' ? activeTab : undefined,
  }

  const { data, isLoading } = useConfigRequests(currentFilters)
  const { data: pendingCount } = useConfigRequestsPendingCount()
  const updateMutation = useUpdateConfigRequest()

  const openDetail = (req: ConfigRequest) => {
    setSelectedRequest(req)
    setShowDetail(true)
  }

  const openUpdate = (req: ConfigRequest, status: UpdateConfigRequestPayload['status']) => {
    setSelectedRequest(req)
    setUpdateForm({ status, admin_notes: req.admin_notes || '', reason: req.reason || '' })
    setShowUpdate(true)
  }

  const handleUpdate = () => {
    if (!selectedRequest) return
    updateMutation.mutate(
      { id: selectedRequest.id, payload: updateForm },
      {
        onSuccess: () => {
          setShowUpdate(false)
          setShowDetail(false)
          setSelectedRequest(null)
        },
      }
    )
  }

  const columns: ColumnDef<ConfigRequest>[] = [
    {
      key: 'user_id',
      header: 'Usuario',
      // Card 115: staff triages by person — name + email; the raw id only
      // remains as fallback for users deleted after requesting.
      cell: (row: ConfigRequest) => (
        <Link
          to="/client-config/$userId"
          params={{ userId: row.user_id }}
          className="block text-primary hover:underline"
        >
          <span className="block text-sm font-medium">
            {row.user_name || row.user_email || row.user_id}
          </span>
          {row.user_name && row.user_email && (
            <span className="block text-xs text-muted-foreground">{row.user_email}</span>
          )}
        </Link>
      ),
    },
    {
      key: 'kind',
      header: 'Tipo',
      cell: (row: ConfigRequest) => KIND_LABELS[row.kind] || row.kind,
    },
    {
      key: 'target',
      header: 'Objetivo',
      cell: (row: ConfigRequest) => TARGET_LABELS[row.target] || row.target,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row: ConfigRequest) => <StatusBadge status={row.status} />,
    },
    {
      key: 'approver_scope',
      header: 'Decide',
      cell: (row: ConfigRequest) => (
        <Badge variant={row.approver_scope === 'org' ? 'warning' : 'default'}>
          {APPROVER_LABELS[row.approver_scope || 'staff']}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: 'Fecha',
      cell: (row: ConfigRequest) => (row.created_at ? formatDate(row.created_at) : '-'),
    },
    {
      key: 'actions',
      header: 'Acciones',
      cell: (row: ConfigRequest) => (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => openDetail(row)}>
            <Eye className="h-4 w-4" />
          </Button>
          {row.status === 'pending' && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openUpdate(row, 'approved')}
                className="text-green-600 hover:text-green-700"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openUpdate(row, 'rejected')}
                className="text-red-600 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
          {row.status === 'approved' && row.target !== 'search_settings' && (
            <Button size="sm" variant="outline" asChild className="gap-1 text-xs">
              <Link
                to="/client-config/$userId"
                params={{ userId: row.user_id }}
                search={{ fromRequest: row.id }}
              >
                Aplicar
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          )}
        </div>
      ),
    },
  ]

  const pendingBadge = pendingCount?.count || 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Solicitudes de Configuracion</h1>
        <p className="text-muted-foreground">
          Revisa y aplica los pedidos de alertas y reportes programados de los usuarios
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v)
          setFilters((f) => ({ ...f, page: 1 }))
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            {TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="gap-2">
                {tab.label}
                {tab.value === 'pending' && pendingBadge > 0 && (
                  <Badge variant="error" className="ml-1 h-5 min-w-5 px-1 text-xs">
                    {pendingBadge}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <Select
            value={filters.approver_scope || 'all'}
            onValueChange={(v) =>
              setFilters((f) => ({ ...f, approver_scope: v === 'all' ? undefined : v, page: 1 }))
            }
          >
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {APPROVER_FILTERS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsContent value={activeTab} className="mt-4">
          <DataTable
            columns={columns}
            data={data?.items || []}
            isLoading={isLoading}
            keyExtractor={(r) => r.id}
            pagination={{
              page: data?.page || 1,
              pages: data?.pages || 1,
              onPageChange: (page) => setFilters((f) => ({ ...f, page })),
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle de Solicitud</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground">Usuario</p>
                  <p className="font-medium">
                    {selectedRequest.user_name || selectedRequest.user_email || (
                      <span className="break-all font-mono text-xs">{selectedRequest.user_id}</span>
                    )}
                  </p>
                  {selectedRequest.user_email && selectedRequest.user_name && (
                    <p className="break-all text-xs text-muted-foreground">
                      {selectedRequest.user_email}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <StatusBadge status={selectedRequest.status} />
                </div>
                <div>
                  <p className="text-muted-foreground">Tipo</p>
                  <p className="font-medium">
                    {KIND_LABELS[selectedRequest.kind] || selectedRequest.kind}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Objetivo</p>
                  <p className="font-medium">
                    {TARGET_LABELS[selectedRequest.target] || selectedRequest.target}
                  </p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-muted-foreground">Payload enviado por el usuario</p>
                {selectedRequest.target === 'search_settings' ? (
                  <SearchSettingsPayloadDetail
                    payload={selectedRequest.payload as Partial<SearchSettingsRequestPayload>}
                  />
                ) : (
                  <div className="space-y-1 rounded-lg bg-muted p-3 font-mono text-xs">
                    {Object.entries(selectedRequest.payload).length === 0 ? (
                      <span className="text-muted-foreground">Sin datos</span>
                    ) : (
                      Object.entries(selectedRequest.payload).map(([key, value]) => (
                        <div key={key} className="flex gap-2">
                          <span className="text-muted-foreground">{key}:</span>
                          <span className="break-all">{JSON.stringify(value)}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {selectedRequest.admin_notes && (
                <div>
                  <p className="text-muted-foreground">Notas internas</p>
                  <p className="font-medium">{selectedRequest.admin_notes}</p>
                </div>
              )}
              {selectedRequest.reason && (
                <div>
                  <p className="text-muted-foreground">Motivo</p>
                  <p className="font-medium">{selectedRequest.reason}</p>
                </div>
              )}
              {selectedRequest.reviewed_by && (
                <div>
                  <p className="text-muted-foreground">Revisado por</p>
                  <p className="font-medium">
                    {selectedRequest.reviewed_by} (
                    {selectedRequest.reviewed_at ? formatDate(selectedRequest.reviewed_at) : ''})
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {selectedRequest && (
              <Button
                variant="outline"
                onClick={() =>
                  selectedRequest && openUpdate(selectedRequest, selectedRequest.status)
                }
              >
                Cambiar estado
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog — free transitions, feedback-style (card 92) */}
      <Dialog open={showUpdate} onOpenChange={setShowUpdate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Actualizar Solicitud</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={updateForm.status}
                onValueChange={(v) =>
                  setUpdateForm((f) => ({
                    ...f,
                    status: v as UpdateConfigRequestPayload['status'],
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="approved">Aprobada</SelectItem>
                  <SelectItem value="rejected">Rechazada</SelectItem>
                  <SelectItem value="applied">Aplicada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Textarea
                value={updateForm.reason || ''}
                onChange={(e) => setUpdateForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="Motivo del rechazo o del cambio de estado..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Notas internas (opcional)</Label>
              <Textarea
                value={updateForm.admin_notes || ''}
                onChange={(e) => setUpdateForm((f) => ({ ...f, admin_notes: e.target.value }))}
                placeholder="Notas visibles solo para el equipo..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdate(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Legible rendering for target="search_settings" payloads (card 111, item 4) —
// the generic key:value dump doesn't translate the distribution mode value.
function SearchSettingsPayloadDetail({
  payload,
}: {
  payload: Partial<SearchSettingsRequestPayload>
}) {
  return (
    <div className="space-y-1 rounded-lg bg-muted p-3 text-xs">
      <div className="flex gap-2">
        <span className="text-muted-foreground">Máximo de créditos:</span>
        <span className="font-medium">{payload.max_credits ?? '-'}</span>
      </div>
      <div className="flex gap-2">
        <span className="text-muted-foreground">Distribución:</span>
        <span className="font-medium">
          {payload.distribution
            ? DISTRIBUTION_MODE_LABELS[payload.distribution] || payload.distribution
            : '-'}
        </span>
      </div>
      {/* Card 116: the detail is what staff actually applies */}
      {payload.custom_distribution && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 pl-2">
          {Object.entries(payload.custom_distribution).map(([platform, pct]) => (
            <span key={platform}>
              <span className="text-muted-foreground">
                {PLATFORM_LABELS[platform] || platform}:
              </span>{' '}
              <span className="font-medium">{pct}%</span>
            </span>
          ))}
        </div>
      )}
      {payload.single_platform && (
        <div className="flex gap-2">
          <span className="text-muted-foreground">Plataforma:</span>
          <span className="font-medium">
            {PLATFORM_LABELS[payload.single_platform] || payload.single_platform}
          </span>
        </div>
      )}
    </div>
  )
}

const PLATFORM_LABELS: Record<string, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  twitter: 'Twitter / X',
  facebook: 'Facebook',
  google_news: 'Google News',
  youtube: 'YouTube',
}
